import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UsuarioService } from '../../core/services/usuario.service';
import { AlertService } from '../../core/services/alert.service';
import { UiLoadingComponent } from '../../shared/components/ui-loading.component';
import { RolApi, UsuarioApi } from '../../core/models/api/usuario-api';

@Component({
    selector: 'app-admin-usuarios',
    standalone: true,
    imports: [CommonModule, FormsModule, UiLoadingComponent],
    template: `
    <div class="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 class="text-2xl font-black text-gray-800">Usuarios y roles</h2>
        <p class="text-sm text-gray-400 mt-1">Asigna roles, retira accesos o desactiva cuentas.</p>
      </div>
      <label class="block min-w-60">
        <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Buscar</span>
        <input [(ngModel)]="key" (ngModelChange)="buscar($event)" placeholder="Correo o nombre…" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
      </label>
    </div>

    <app-ui-loading *ngIf="loading()"></app-ui-loading>

    <div *ngIf="!loading()" class="bg-white rounded-[2rem] border border-rose-100 shadow-sm overflow-hidden">
      <table class="w-full text-sm">
        <thead>
          <tr class="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
            <th class="px-5 py-3">Usuario</th>
            <th class="px-5 py-3">Roles</th>
            <th class="px-5 py-3">Estado</th>
            <th class="px-5 py-3">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let u of usuarios()" class="border-b border-gray-50 align-top">
            <td class="px-5 py-3">
              <p class="font-black text-gray-800">{{ u.nombreVisible }}</p>
              <p class="text-xs text-gray-400">{{ u.correo }}</p>
            </td>
            <td class="px-5 py-3">
              <div class="flex flex-wrap gap-1 mb-2">
                <span *ngFor="let r of u.roles" class="text-[10px] font-bold uppercase px-2 py-1 rounded-full bg-rose-50 text-rose-500 flex items-center gap-1">
                  {{ r }}
                  <button type="button" (click)="quitarRol(u, r)" title="Quitar rol" class="cursor-pointer font-black">×</button>
                </span>
                <span *ngIf="u.roles.length === 0" class="text-xs text-gray-300">Sin roles</span>
              </div>
              <div class="flex gap-1">
                <select [(ngModel)]="rolPick[u.uuid]" class="rounded-xl border border-gray-200 px-2 py-1.5 text-xs bg-white outline-none">
                  <option value="">+ Rol…</option>
                  <option *ngFor="let r of roles()" [value]="r.uuid">{{ r.nombre }}</option>
                </select>
                <button type="button" (click)="darRol(u)" [disabled]="!rolPick[u.uuid]" class="px-3 py-1.5 rounded-xl text-white text-xs font-bold disabled:opacity-40 cursor-pointer" style="background-color: var(--bg-main);">Dar</button>
              </div>
            </td>
            <td class="px-5 py-3">
              <span class="text-[10px] font-bold uppercase px-2 py-1 rounded-full" [class.bg-emerald-50]="u.isActive" [class.text-emerald-600]="u.isActive" [class.bg-gray-100]="!u.isActive" [class.text-gray-500]="!u.isActive">
                {{ u.isActive ? 'Activo' : 'Inactivo' }}
              </span>
            </td>
            <td class="px-5 py-3">
              <button type="button" (click)="toggle(u)" class="px-3 py-1.5 rounded-xl border border-gray-200 text-xs font-bold text-gray-600 cursor-pointer">
                {{ u.isActive ? 'Desactivar' : 'Activar' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <p *ngIf="usuarios().length === 0" class="px-5 py-8 text-center text-sm text-gray-400">Sin resultados.</p>
      <div class="flex items-center justify-between px-5 py-3 border-t border-gray-100 text-sm">
        <button type="button" (click)="pagina(false)" [disabled]="page() <= 1" class="px-4 py-2 rounded-xl border border-gray-200 font-bold text-gray-600 disabled:opacity-40 cursor-pointer">Anterior</button>
        <span class="text-xs text-gray-400">Página {{ page() }} de {{ totalPages() }}</span>
        <button type="button" (click)="pagina(true)" [disabled]="page() >= totalPages()" class="px-4 py-2 rounded-xl border border-gray-200 font-bold text-gray-600 disabled:opacity-40 cursor-pointer">Siguiente</button>
      </div>
    </div>
    `
})
export class AdminUsuariosComponent implements OnInit {
    private usuariosService = inject(UsuarioService);
    private alerts = inject(AlertService);

    loading = signal(true);
    usuarios = signal<UsuarioApi[]>([]);
    roles = signal<RolApi[]>([]);
    rolPick: Record<string, string> = {};
    key = signal('');
    page = signal(1);
    total = signal(0);
    pageSize = 9;
    private searchTimer: ReturnType<typeof setTimeout> | null = null;

    ngOnInit(): void {
        this.usuariosService.listRoles().subscribe({
            next: (rows) => this.roles.set(rows),
            error: () => undefined
        });
        this.cargar();
    }

    totalPages(): number {
        return Math.max(1, Math.ceil(this.total() / this.pageSize));
    }

    buscar(term: string) {
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }
        this.searchTimer = setTimeout(() => {
            this.page.set(1);
            this.cargar();
        }, 400);
    }

    pagina(next: boolean) {
        this.page.update((p) => next ? p + 1 : Math.max(1, p - 1));
        this.cargar();
    }

    darRol(u: UsuarioApi) {
        const rolUuid = this.rolPick[u.uuid];
        if (!rolUuid) {
            return;
        }
        this.usuariosService.assignRol(u.uuid, rolUuid).subscribe({
            next: (tx) => {
                this.alerts.ok(tx.message);
                this.rolPick[u.uuid] = '';
                this.cargar();
            },
            error: (err) => this.alerts.error(err?.message || 'No se pudo asignar el rol.')
        });
    }

    quitarRol(u: UsuarioApi, codigo: string) {
        const rol = this.roles().find((r) => r.codigo === codigo);
        if (!rol) {
            this.alerts.error('Rol no encontrado en el catálogo.');
            return;
        }
        this.usuariosService.removeRol(u.uuid, rol.uuid).subscribe({
            next: (tx) => {
                this.alerts.ok(tx.message);
                this.cargar();
            },
            error: (err) => this.alerts.error(err?.message || 'No se pudo retirar el rol.')
        });
    }

    toggle(u: UsuarioApi) {
        this.usuariosService.toggleActivo(u.uuid).subscribe({
            next: (tx) => {
                this.alerts.ok(tx.message);
                this.cargar();
            },
            error: (err) => this.alerts.error(err?.message || 'No se pudo cambiar el estado.')
        });
    }

    private cargar() {
        this.loading.set(true);
        this.usuariosService.listUsuarios(this.key(), this.page(), this.pageSize).subscribe({
            next: (res) => {
                this.usuarios.set(res.data);
                this.total.set(res.total);
                this.loading.set(false);
            },
            error: (err) => {
                this.loading.set(false);
                this.alerts.error(err?.message || 'No se pudieron cargar los usuarios.');
            }
        });
    }
}
